import { computed, ref } from 'vue';
import type { TrainingMatrixPositionRow } from '@/entities/Training';

export function useTrainingMatrixSelection() {
  const selectedIds = ref(new Set<number>());
  // The selection outlives the page it was made on, so it keeps which ids had documents when
  // they were last on screen: the bulk delete only removes entries of positions that have them.
  const idsWithDocuments = ref(new Set<number>());

  const selectedCount = computed(() => selectedIds.value.size);
  const hasSelected = computed(() => selectedIds.value.size > 0);
  const selectedIdsWithDocuments = computed(() =>
    Array.from(selectedIds.value).filter((id) => idsWithDocuments.value.has(id))
  );
  const hasSelectedWithDocuments = computed(() => selectedIdsWithDocuments.value.length > 0);

  function isSelected(id: number): boolean {
    return selectedIds.value.has(id);
  }

  function toggle(id: number): void {
    const next = new Set(selectedIds.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    selectedIds.value = next;
  }

  function toggleAll(ids: number[]): void {
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.value.has(id));
    selectedIds.value = allSelected ? new Set() : new Set(ids);
  }

  function syncDocuments(positions: TrainingMatrixPositionRow[]): void {
    const next = new Set(idsWithDocuments.value);
    for (const position of positions) {
      if (position.documents.length > 0) {
        next.add(position.id);
      } else {
        next.delete(position.id);
      }
    }
    idsWithDocuments.value = next;
  }

  function markWithoutDocuments(id: number): void {
    const next = new Set(idsWithDocuments.value);
    next.delete(id);
    idsWithDocuments.value = next;
  }

  function clear(): void {
    selectedIds.value = new Set();
  }

  function isAllSelected(ids: number[]): boolean {
    return ids.length > 0 && ids.every((id) => selectedIds.value.has(id));
  }

  return {
    selectedIds,
    selectedCount,
    hasSelected,
    selectedIdsWithDocuments,
    hasSelectedWithDocuments,
    isSelected,
    toggle,
    toggleAll,
    isAllSelected,
    syncDocuments,
    markWithoutDocuments,
    clear,
  };
}
